import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCms } from '../context/CmsContext';
import NoIndex from '../components/NoIndex';
import { LogOut, Palette, FileText, Wine, Mail, Plus, Trash2, Save, X, Upload, Image as ImageIcon, Camera, Users, Images, Moon, Sun } from 'lucide-react';
import { fileToResizedDataUrl } from '../utils/imageResize';

const TEXT_GROUPS = [
  {
    label: 'Home — Hero',
    keys: [
      ['home.hero_title', 'Hero titel'],
      ['home.hero_subtitle', 'Hero ondertitel', 'textarea'],
    ],
  },
  {
    label: 'Home — Welkom / Introductie',
    keys: [
      ['home.intro_title', 'Welkom titel'],
      ['home.intro_text', 'Welkom tekst', 'textarea'],
    ],
  },
  {
    label: 'Home — Beach Club & Quote',
    keys: [
      ['home.parallax_quote', 'Quote (op parallax foto)', 'textarea'],
      ['home.experience_title', 'Beach Club titel'],
      ['home.experience_text', 'Beach Club tekst', 'textarea'],
    ],
  },
  {
    label: 'Home — Call to Action',
    keys: [
      ['home.cta_title', 'CTA titel'],
      ['home.cta_text', 'CTA tekst', 'textarea'],
    ],
  },
  {
    label: 'Over Ons',
    keys: [
      ['overons.hero_title', 'Hero titel'],
      ['overons.hero_subtitle', 'Hero ondertitel', 'textarea'],
      ['overons.story_title', 'Verhaal titel'],
      ['overons.story_p1', 'Verhaal alinea 1', 'textarea'],
      ['overons.story_p2', 'Verhaal alinea 2', 'textarea'],
      ['overons.story_p3', 'Verhaal alinea 3', 'textarea'],
      ['overons.values_title', 'Kernwaarden titel'],
    ],
  },
  {
    label: 'Hoe Werkt Het?',
    keys: [
      ['hwh.hero_title', 'Hero titel'],
      ['hwh.hero_subtitle', 'Hero ondertitel', 'textarea'],
      ['hwh.step1_title', 'Stap 1 titel'],
      ['hwh.step1_text', 'Stap 1 tekst', 'textarea'],
      ['hwh.step2_title', 'Stap 2 titel'],
      ['hwh.step2_text', 'Stap 2 tekst', 'textarea'],
      ['hwh.step3_title', 'Stap 3 titel'],
      ['hwh.step3_text', 'Stap 3 tekst', 'textarea'],
      ['hwh.step4_title', 'Stap 4 titel'],
      ['hwh.step4_text', 'Stap 4 tekst', 'textarea'],
      ['hwh.pricing_title', 'Prijzen titel'],
      ['hwh.pricing_text', 'Prijzen tekst', 'textarea'],
    ],
  },
  {
    label: 'FAQ',
    keys: [
      ['faq.hero_title', 'Hero titel'],
      ['faq.hero_subtitle', 'Hero ondertitel', 'textarea'],
    ],
  },
  {
    label: 'Contact',
    keys: [
      ['contact.hero_title', 'Hero titel'],
      ['contact.hero_subtitle', 'Hero ondertitel', 'textarea'],
      ['contact.werkgebied', 'Werkgebied'],
      ['contact.bereikbaarheid', 'Bereikbaarheid'],
      ['contact.whatsapp_number', 'WhatsApp nummer (zonder +, bv. 31612345678)'],
      ['contact.email', 'Contact e-mail'],
      ['contact.phone', 'Telefoonnummer (weergave)'],
      ['contact.instagram', 'Instagram URL'],
    ],
  },
];

const formatError = (detail) => {
  if (!detail) return 'Er ging iets mis.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const TabButton = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-2 px-5 py-3 rounded-lg font-normal transition-all ${
      active ? 'bg-cm-primary text-white shadow-elegant' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
    }`}
  >
    <Icon size={18} />
    {label}
  </button>
);

// ------------- Kleuren tab ----------------
const ColorsTab = ({ onSaved }) => {
  const { primaryColor, setPrimaryColor } = useCms();
  const { authedAxios } = useAuth();
  const [color, setColor] = useState(primaryColor);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => setColor(primaryColor), [primaryColor]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      await authedAxios().put('/api/admin/cms', { primary_color: color });
      setPrimaryColor(color);
      onSaved?.();
      setMsg('Kleur opgeslagen!');
    } catch (e) {
      setMsg(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 2500);
    }
  };

  const presets = ['#13498f', '#0a8a4e', '#c0392b', '#8e44ad', '#d35400', '#2c3e50', '#e91e63', '#00897b'];

  return (
    <div className="bg-white p-8 rounded-2xl shadow-elegant border border-gray-100">
      <h2 className="text-2xl font-light text-cm-primary mb-2">Hoofdkleur</h2>
      <p className="text-sm text-gray-500 mb-6">De hele website past zich automatisch aan deze kleur aan.</p>

      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={color}
            onChange={(e) => {
              setColor(e.target.value);
              setPrimaryColor(e.target.value); // live preview
            }}
            className="w-20 h-20 rounded-lg cursor-pointer border border-gray-200"
            data-testid="cms-color-picker"
          />
          <input
            type="text"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 font-mono w-32"
            data-testid="cms-color-hex"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p}
              onClick={() => {
                setColor(p);
                setPrimaryColor(p);
              }}
              className="w-9 h-9 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform"
              style={{ backgroundColor: p }}
              title={p}
            />
          ))}
        </div>
      </div>

      <div className="mt-8 flex items-center gap-3">
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="cms-color-save">
          <Save size={18} /> {saving ? 'Opslaan...' : 'Kleur Opslaan'}
        </button>
        {msg && <span className="text-sm text-gray-600">{msg}</span>}
      </div>
    </div>
  );
};

// ------------- Teksten tab ----------------
const TextsTab = () => {
  const { texts, setTexts } = useCms();
  const { authedAxios } = useAuth();
  const [form, setForm] = useState(texts);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => setForm(texts), [texts]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const { data } = await authedAxios().put('/api/admin/cms', { texts: form });
      setTexts(data.texts);
      setMsg('Teksten opgeslagen!');
    } catch (e) {
      setMsg(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 2500);
    }
  };

  return (
    <div className="space-y-6">
      {TEXT_GROUPS.map((group) => (
        <div key={group.label} className="bg-white p-8 rounded-2xl shadow-elegant border border-gray-100">
          <h2 className="text-2xl font-light text-cm-primary mb-6">{group.label}</h2>
          <div className="space-y-4">
            {group.keys.map(([key, label, type]) => (
              <div key={key}>
                <label className="block text-sm text-gray-700 mb-2">{label}</label>
                {type === 'textarea' ? (
                  <textarea
                    rows={4}
                    value={form[key] ?? ''}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-cm-primary focus:ring-1 focus:ring-cm-primary outline-none"
                    data-testid={`cms-text-${key}`}
                  />
                ) : (
                  <input
                    type="text"
                    value={form[key] ?? ''}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-cm-primary focus:ring-1 focus:ring-cm-primary outline-none"
                    data-testid={`cms-text-${key}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-3 sticky bottom-4 bg-white/80 backdrop-blur p-4 rounded-xl">
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="cms-texts-save">
          <Save size={18} /> {saving ? 'Opslaan...' : 'Alle Teksten Opslaan'}
        </button>
        {msg && <span className="text-sm text-gray-600">{msg}</span>}
      </div>
    </div>
  );
};

// ------------- Cocktails tab ----------------
const emptyCocktail = {
  naam: '',
  beschrijving: '',
  ingredienten: '',
  prijs: 6,
  categorie: '',
  categorieen: [],
  afbeelding: '',
  populair: false,
};

const CocktailPhotoField = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErr('');
    setUploading(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file, 1200, 0.85);
      onChange(dataUrl);
    } catch (ex) {
      setErr(ex.message || 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex flex-col sm:flex-row gap-4 items-start">
      <div
        className="w-40 h-28 rounded-lg border border-gray-200 bg-gray-100 bg-cover bg-center flex items-center justify-center text-gray-400"
        style={value ? { backgroundImage: `url("${value}")` } : {}}
      >
        {!value && <ImageIcon size={28} />}
      </div>
      <div className="flex-1 space-y-2">
        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-cm-primary text-cm-primary hover:bg-gray-50 cursor-pointer text-sm" data-testid="cms-cocktail-upload">
          <Upload size={16} /> {uploading ? 'Bezig...' : (value ? 'Vervang foto' : 'Foto uploaden')}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
        </label>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="ml-2 text-sm text-red-600 hover:underline inline-flex items-center gap-1"
          >
            <Trash2 size={14} /> Foto verwijderen
          </button>
        )}
        <div className="text-xs text-gray-500">JPG / PNG. Wordt automatisch verkleind.</div>
        {err && <div className="text-xs text-red-600">{err}</div>}
      </div>
    </div>
  );
};

const CocktailsTab = () => {
  const { authedAxios } = useAuth();
  const { cocktailCategories, setCocktailCategories } = useCms();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // id or 'new'
  const [form, setForm] = useState(emptyCocktail);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [newCatMode, setNewCatMode] = useState(false);
  const [newCatValue, setNewCatValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get('/api/cocktails');
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setEditing('new');
    setForm(emptyCocktail);
  };

  const startEdit = (c) => {
    setEditing(c.id);
    const cats = (c.categorieen && c.categorieen.length) ? c.categorieen : (c.categorie ? [c.categorie] : []);
    setForm({ ...c, ingredienten: c.ingredienten.join(', '), categorieen: cats, categorie: cats[0] || '' });
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyCocktail);
  };

  const save = async () => {
    const payload = {
      ...form,
      prijs: Number(form.prijs),
      ingredienten: form.ingredienten
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      categorieen: form.categorieen || [],
      categorie: (form.categorieen && form.categorieen[0]) || form.categorie || '',
    };
    try {
      if (editing === 'new') {
        await authedAxios().post('/api/admin/cocktails', payload);
      } else {
        await authedAxios().put(`/api/admin/cocktails/${editing}`, payload);
      }
      setMsg('Opgeslagen!');
      cancel();
      await load();
    } catch (e) {
      setMsg(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setTimeout(() => setMsg(''), 2500);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Cocktail verwijderen?')) return;
    try {
      await authedAxios().delete(`/api/admin/cocktails/${id}`);
      await load();
    } catch (e) {
      setMsg(formatError(e.response?.data?.detail) || e.message);
      setTimeout(() => setMsg(''), 2500);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-light text-cm-primary">Cocktails ({items.length})</h2>
        <button onClick={startNew} className="btn-primary inline-flex items-center gap-2" data-testid="cms-cocktail-new">
          <Plus size={18} /> Nieuwe Cocktail
        </button>
      </div>

      {editing && (
        <div className="bg-white p-6 rounded-2xl shadow-elegant border-2 border-cm-primary">
          <h3 className="text-lg font-medium mb-4 text-cm-primary">
            {editing === 'new' ? 'Nieuwe cocktail toevoegen' : 'Cocktail bewerken'}
          </h3>

          {/* Photo */}
          <CocktailPhotoField
            value={form.afbeelding}
            onChange={(v) => setForm({ ...form, afbeelding: v })}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <input className="border border-gray-300 rounded-lg px-3 py-2" placeholder="Naam" value={form.naam}
              onChange={(e) => setForm({ ...form, naam: e.target.value })} data-testid="cms-cocktail-naam" />
            <div className="md:col-span-1">
              <div className="text-sm text-gray-700 mb-2">Categorieën (meerdere mogelijk)</div>
              <div className="flex flex-wrap gap-2">
                {cocktailCategories.map((c) => {
                  const active = (form.categorieen || []).includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        const cur = form.categorieen || [];
                        const next = active ? cur.filter((x) => x !== c) : [...cur, c];
                        setForm({ ...form, categorieen: next });
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        active ? 'bg-cm-primary text-white border-cm-primary' : 'bg-white text-gray-700 border-gray-300 hover:border-cm-primary'
                      }`}
                      data-testid={`cms-cat-toggle-${c}`}
                    >
                      {active ? '✓ ' : ''}{c}
                    </button>
                  );
                })}
                {!newCatMode ? (
                  <button
                    type="button"
                    onClick={() => { setNewCatMode(true); setNewCatValue(''); }}
                    className="px-3 py-1.5 rounded-full text-sm border border-dashed border-cm-primary text-cm-primary hover:bg-gray-50"
                  >
                    + Nieuwe categorie
                  </button>
                ) : (
                  <div className="flex items-center gap-1">
                    <input
                      className="border border-cm-primary rounded-full px-3 py-1.5 text-sm"
                      placeholder="Nieuwe categorie"
                      value={newCatValue}
                      autoFocus
                      onChange={(e) => setNewCatValue(e.target.value)}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const v = newCatValue.trim();
                          if (!v) return;
                          const next = [...new Set([...cocktailCategories, v])];
                          try {
                            const { data } = await authedAxios().put('/api/admin/cms', { cocktail_categories: next });
                            setCocktailCategories(data.cocktail_categories);
                            setForm({ ...form, categorieen: [...(form.categorieen || []), v] });
                            setNewCatMode(false); setNewCatValue('');
                          } catch (err) {
                            setMsg(err.response?.data?.detail || err.message);
                            setTimeout(() => setMsg(''), 2500);
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => { setNewCatMode(false); setNewCatValue(''); }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
            <input type="number" step="0.01" className="border border-gray-300 rounded-lg px-3 py-2" placeholder="Prijs" value={form.prijs}
              onChange={(e) => setForm({ ...form, prijs: e.target.value })} />
            <input className="md:col-span-2 border border-gray-300 rounded-lg px-3 py-2" placeholder="Ingrediënten (komma-gescheiden)"
              value={form.ingredienten} onChange={(e) => setForm({ ...form, ingredienten: e.target.value })} />
            <textarea rows={3} className="md:col-span-2 border border-gray-300 rounded-lg px-3 py-2" placeholder="Beschrijving"
              value={form.beschrijving} onChange={(e) => setForm({ ...form, beschrijving: e.target.value })} />
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!form.populair} onChange={(e) => setForm({ ...form, populair: e.target.checked })} />
              Populair
            </label>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={save} className="btn-primary inline-flex items-center gap-2" data-testid="cms-cocktail-save">
              <Save size={18} /> Opslaan
            </button>
            <button onClick={cancel} className="px-5 py-2 rounded-lg border border-gray-300 inline-flex items-center gap-2">
              <X size={18} /> Annuleren
            </button>
          </div>
        </div>
      )}

      {msg && <div className="text-sm text-gray-600">{msg}</div>}

      {loading ? (
        <div className="text-gray-500">Laden...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} className="bg-white p-5 rounded-xl shadow-elegant border border-gray-100">
              {c.afbeelding && (
                <div className="h-32 rounded-lg bg-gray-100 mb-3 bg-cover bg-center"
                  style={{ backgroundImage: `url(${c.afbeelding})` }} />
              )}
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium text-cm-primary">{c.naam}</div>
                  <div className="text-xs text-gray-500">{c.categorie} · €{c.prijs}{c.populair ? ' · ★' : ''}</div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2 line-clamp-2">{c.beschrijving}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => startEdit(c)} className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50"
                  data-testid={`cms-cocktail-edit-${c.id}`}>Bewerken</button>
                <button onClick={() => remove(c.id)} className="text-sm px-3 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                  data-testid={`cms-cocktail-delete-${c.id}`}>
                  <Trash2 size={14} /> Verwijder
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ------------- Offertes tab ----------------
const OffertesTab = () => {
  const { authedAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get('/api/admin/offertes');
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await authedAxios().put(`/api/admin/offertes/${id}`, { status });
      await load();
    } catch (e) {
      setMsg(formatError(e.response?.data?.detail) || e.message);
      setTimeout(() => setMsg(''), 2500);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Offerte verwijderen?')) return;
    try {
      await authedAxios().delete(`/api/admin/offertes/${id}`);
      await load();
    } catch (e) {
      setMsg(formatError(e.response?.data?.detail) || e.message);
      setTimeout(() => setMsg(''), 2500);
    }
  };

  const statusColor = (s) => ({
    nieuw: 'bg-blue-100 text-blue-800',
    bekeken: 'bg-yellow-100 text-yellow-800',
    beantwoord: 'bg-green-100 text-green-800',
  }[s] || 'bg-gray-100 text-gray-700');

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-light text-cm-primary">Binnenkomende Offertes ({items.length})</h2>
      {msg && <div className="text-sm text-red-600">{msg}</div>}
      {loading ? (
        <div className="text-gray-500">Laden...</div>
      ) : items.length === 0 ? (
        <div className="bg-white p-8 rounded-xl text-center text-gray-500 border border-gray-100">
          Nog geen offerte aanvragen.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((o) => (
            <div key={o.id} className="bg-white p-5 rounded-xl shadow-elegant border border-gray-100" data-testid={`cms-offerte-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-cm-primary">
                    {o.naam}{o.bedrijfsnaam ? ` · ${o.bedrijfsnaam}` : ''}
                  </div>
                  <div className="text-sm text-gray-600">{o.email} · {o.telefoonnummer}</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {o.soort_evenement} · {o.aantal_gasten} gasten · {o.datum_evenement} · {o.locatie}
                  </div>
                  {o.adres && (
                    <div className="text-sm text-gray-700 mt-1"><strong>Adres:</strong> {o.adres}</div>
                  )}
                  {o.aantal_cocktails != null && (
                    <div className="text-sm text-gray-700 mt-1"><strong>Aantal cocktails:</strong> {o.aantal_cocktails}</div>
                  )}
                  {o.gewenste_cocktails && (
                    <div className="text-sm text-gray-700 mt-2"><strong>Gewenste cocktails:</strong> {o.gewenste_cocktails}</div>
                  )}
                  {o.opmerkingen && (
                    <div className="text-sm text-gray-700 mt-1"><strong>Opmerkingen:</strong> {o.opmerkingen}</div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${statusColor(o.status)}`}>{o.status}</span>
                  <select
                    value={o.status}
                    onChange={(e) => updateStatus(o.id, e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="nieuw">nieuw</option>
                    <option value="bekeken">bekeken</option>
                    <option value="beantwoord">beantwoord</option>
                  </select>
                  <button onClick={() => remove(o.id)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1">
                    <Trash2 size={12} /> Verwijderen
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ------------- Photos & Team tab ----------------
const PHOTO_GROUPS = [
  {
    label: 'Home pagina',
    keys: [
      ['home.hero', 'Hoofdfoto (achter "Cocktails op Locatie")'],
      ['home.parallax1', 'Sfeerfoto met quote (eerste parallax)'],
      ['home.parallax2', 'Sfeerfoto Beach Club ervaring (tweede parallax)'],
    ],
  },
  {
    label: 'Pagina banners (bovenkant van elke pagina)',
    keys: [
      ['banner.overons', 'Over Ons'],
      ['banner.hwh', 'Hoe werkt het?'],
      ['banner.menu', 'Cocktailmenu'],
      ['banner.galerij', 'Galerij'],
      ['banner.faq', 'FAQ'],
      ['banner.contact', 'Contact'],
    ],
  },
];

const ImageUploadField = ({ value, onChange, label }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErr('');
    setUploading(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file, 1600, 0.85);
      onChange(dataUrl);
    } catch (ex) {
      setErr(ex.message || 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <p className="text-sm font-medium text-gray-700 mb-3">{label}</p>
      <div className="flex flex-col sm:flex-row gap-4">
        <div
          className="w-56 h-32 rounded-lg border border-gray-200 bg-gray-100 bg-cover bg-center flex items-center justify-center text-gray-400 flex-shrink-0"
          style={value ? { backgroundImage: `url("${value}")` } : {}}
        >
          {!value && <ImageIcon size={28} />}
        </div>
        <div className="flex-1 space-y-2">
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-cm-primary text-cm-primary hover:bg-gray-50 cursor-pointer text-sm">
            <Upload size={16} /> {uploading ? 'Bezig...' : (value ? 'Vervang foto' : 'Foto uploaden')}
            <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
          </label>
          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="ml-2 text-sm text-red-600 hover:underline inline-flex items-center gap-1"
            >
              <Trash2 size={14} /> Foto verwijderen
            </button>
          )}
          <div className="text-xs text-gray-500">Of plak een afbeelding URL:</div>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="https://..."
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={() => { if (urlInput.trim()) { onChange(urlInput.trim()); setUrlInput(''); } }}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              Gebruik
            </button>
          </div>
          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>
      </div>
    </div>
  );
};

const PhotosTab = () => {
  const { images, setImages } = useCms();
  const { authedAxios } = useAuth();
  const [form, setForm] = useState(images);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => setForm(images), [images]);

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const { data } = await authedAxios().put('/api/admin/cms', { images: form });
      setImages(data.images);
      setMsg('Foto\'s opgeslagen!');
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 2500);
    }
  };

  return (
    <div className="space-y-6">
      {PHOTO_GROUPS.map((group) => (
        <div key={group.label}>
          <h2 className="text-2xl font-light text-cm-primary mb-4">{group.label}</h2>
          <div className="space-y-3">
            {group.keys.map(([key, label]) => (
              <ImageUploadField
                key={key}
                label={label}
                value={form[key] || ''}
                onChange={(v) => setForm({ ...form, [key]: v })}
              />
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-3 sticky bottom-4 bg-white/90 backdrop-blur p-4 rounded-xl shadow-elegant">
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
          <Save size={18} /> {saving ? 'Opslaan...' : 'Alle Foto\'s Opslaan'}
        </button>
        {msg && <span className="text-sm text-gray-600">{msg}</span>}
      </div>
    </div>
  );
};

// ------------- Team tab ----------------
const TeamTab = () => {
  const { team, setTeam } = useCms();
  const { authedAxios } = useAuth();
  const [form, setForm] = useState(team);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [uploadingIdx, setUploadingIdx] = useState(null);

  useEffect(() => setForm(team), [team]);

  // Always ensure 3 slots
  useEffect(() => {
    if (form.length < 3) {
      const filled = [...form];
      while (filled.length < 3) filled.push({ name: '', role: '', photo: '', bio: '' });
      setForm(filled);
    }
  }, [form]);

  const updateMember = (idx, patch) => {
    const next = form.map((m, i) => (i === idx ? { ...m, ...patch } : m));
    setForm(next);
  };

  const uploadPhoto = async (idx, file) => {
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const dataUrl = await fileToResizedDataUrl(file, 1000, 0.85);
      updateMember(idx, { photo: dataUrl });
    } catch (ex) {
      setMsg(ex.message || 'Upload mislukt');
      setTimeout(() => setMsg(''), 2500);
    } finally {
      setUploadingIdx(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const { data } = await authedAxios().put('/api/admin/cms', { team: form });
      setTeam(data.team);
      setMsg('Team opgeslagen!');
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 2500);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-light text-cm-primary mb-2">Ons Team</h2>
        <p className="text-sm text-gray-500 mb-6">
          Toon tot 3 teamleden op de &ldquo;Over Ons&rdquo; pagina. Laat een teamlid leeg om hem te verbergen.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {form.map((m, idx) => (
          <div key={idx} className="bg-white rounded-2xl shadow-elegant border border-gray-100 p-5 space-y-3">
            <div
              className="h-48 rounded-lg bg-gray-100 bg-cover bg-center flex items-center justify-center text-gray-400"
              style={m.photo ? { backgroundImage: `url("${m.photo}")` } : {}}
            >
              {!m.photo && <ImageIcon size={32} />}
            </div>
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-cm-primary text-cm-primary hover:bg-gray-50 cursor-pointer text-sm">
              <Upload size={14} /> {uploadingIdx === idx ? 'Bezig...' : (m.photo ? 'Vervang foto' : 'Foto uploaden')}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  uploadPhoto(idx, f);
                }}
              />
            </label>
            {m.photo && (
              <button
                type="button"
                onClick={() => updateMember(idx, { photo: '' })}
                className="ml-2 text-xs text-red-600 hover:underline"
              >
                Verwijder foto
              </button>
            )}
            <input
              type="text"
              placeholder="Naam"
              value={m.name}
              onChange={(e) => updateMember(idx, { name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <input
              type="text"
              placeholder="Rol (bv. Hoofdbartender)"
              value={m.role}
              onChange={(e) => updateMember(idx, { role: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <textarea
              rows={3}
              placeholder="Korte beschrijving / bio"
              value={m.bio}
              onChange={(e) => updateMember(idx, { bio: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 sticky bottom-4 bg-white/90 backdrop-blur p-4 rounded-xl shadow-elegant">
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
          <Save size={18} /> {saving ? 'Opslaan...' : 'Team Opslaan'}
        </button>
        {msg && <span className="text-sm text-gray-600">{msg}</span>}
      </div>
    </div>
  );
};

// ------------- Galerij tab ----------------
const GALERIJ_CATEGORIES = ['Bruiloften', 'Bedrijfsfeesten', 'Festivals', 'Cocktails'];
const emptyGalerij = { titel: '', beschrijving: '', afbeelding: '', categorie: 'Cocktails', volgorde: 1 };

const GalerijTab = () => {
  const { authedAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyGalerij);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get('/api/admin/galerij');
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing('new'); setForm(emptyGalerij); };
  const startEdit = (item) => { setEditing(item.id); setForm({ ...item }); };
  const cancel = () => { setEditing(null); setForm(emptyGalerij); };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file, 1600, 0.85);
      setForm((f) => ({ ...f, afbeelding: dataUrl }));
    } catch (e) {
      setMsg(e.message || 'Upload mislukt');
      setTimeout(() => setMsg(''), 2500);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.titel.trim()) { setMsg('Titel is verplicht'); setTimeout(() => setMsg(''), 2500); return; }
    if (!form.afbeelding) { setMsg('Foto is verplicht'); setTimeout(() => setMsg(''), 2500); return; }
    try {
      const payload = { ...form, volgorde: Number(form.volgorde) || 1 };
      if (editing === 'new') {
        await authedAxios().post('/api/admin/galerij', payload);
      } else {
        await authedAxios().put(`/api/admin/galerij/${editing}`, payload);
      }
      setMsg('Opgeslagen!');
      cancel();
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setTimeout(() => setMsg(''), 2500);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Foto uit galerij verwijderen?')) return;
    try {
      await authedAxios().delete(`/api/admin/galerij/${id}`);
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
      setTimeout(() => setMsg(''), 2500);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-light text-cm-primary">Galerij ({items.length})</h2>
        <button onClick={startNew} className="btn-primary inline-flex items-center gap-2" data-testid="cms-galerij-new">
          <Plus size={18} /> Nieuwe foto
        </button>
      </div>

      {editing && (
        <div className="bg-white p-6 rounded-2xl shadow-elegant border-2 border-cm-primary">
          <h3 className="text-lg font-medium mb-4 text-cm-primary">
            {editing === 'new' ? 'Foto toevoegen' : 'Foto bewerken'}
          </h3>

          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div
              className="w-56 h-40 rounded-lg border border-gray-200 bg-gray-100 bg-cover bg-center flex items-center justify-center text-gray-400 flex-shrink-0"
              style={form.afbeelding ? { backgroundImage: `url("${form.afbeelding}")` } : {}}
            >
              {!form.afbeelding && <ImageIcon size={32} />}
            </div>
            <div className="flex-1 space-y-2">
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-cm-primary text-cm-primary hover:bg-gray-50 cursor-pointer text-sm">
                <Upload size={16} /> {uploading ? 'Bezig...' : (form.afbeelding ? 'Vervang foto' : 'Foto uploaden')}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadPhoto(f); }}
                  disabled={uploading}
                />
              </label>
              {form.afbeelding && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, afbeelding: '' })}
                  className="ml-2 text-sm text-red-600 hover:underline inline-flex items-center gap-1"
                >
                  <Trash2 size={14} /> Verwijderen
                </button>
              )}
              <div className="text-xs text-gray-500">JPG / PNG. Wordt automatisch verkleind.</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              className="border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Titel *"
              value={form.titel}
              onChange={(e) => setForm({ ...form, titel: e.target.value })}
            />
            <select
              className="border border-gray-300 rounded-lg px-3 py-2"
              value={form.categorie}
              onChange={(e) => setForm({ ...form, categorie: e.target.value })}
            >
              {GALERIJ_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <textarea
              rows={2}
              className="md:col-span-2 border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Beschrijving (optioneel)"
              value={form.beschrijving || ''}
              onChange={(e) => setForm({ ...form, beschrijving: e.target.value })}
            />
            <input
              type="number"
              min="1"
              className="border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Volgorde"
              value={form.volgorde}
              onChange={(e) => setForm({ ...form, volgorde: e.target.value })}
            />
          </div>

          <div className="flex gap-3 mt-4">
            <button onClick={save} className="btn-primary inline-flex items-center gap-2">
              <Save size={18} /> Opslaan
            </button>
            <button onClick={cancel} className="px-5 py-2 rounded-lg border border-gray-300 inline-flex items-center gap-2">
              <X size={18} /> Annuleren
            </button>
          </div>
        </div>
      )}

      {msg && <div className="text-sm text-gray-600">{msg}</div>}

      {loading ? (
        <div className="text-gray-500">Laden...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="bg-white p-4 rounded-xl shadow-elegant border border-gray-100">
              <div className="h-40 rounded-lg bg-gray-100 mb-3 bg-cover bg-center"
                style={it.afbeelding ? { backgroundImage: `url("${it.afbeelding}")` } : {}} />
              <div className="font-medium text-cm-primary">{it.titel}</div>
              <div className="text-xs text-gray-500">{it.categorie} · #{it.volgorde}</div>
              {it.beschrijving && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{it.beschrijving}</p>}
              <div className="flex gap-2 mt-3">
                <button onClick={() => startEdit(it)} className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50">Bewerken</button>
                <button onClick={() => remove(it.id)} className="text-sm px-3 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1">
                  <Trash2 size={14} /> Verwijder
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ------------- Main dashboard ----------------
const THEME_KEY = 'cm_admin_theme';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('kleuren');
  const [dark, setDark] = useState(() => {
    try {
      return localStorage.getItem(THEME_KEY) === 'dark';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light');
    } catch {
      /* localStorage blocked — non-fatal */
    }
  }, [dark]);

  const toggleTheme = () => setDark((v) => !v);

  return (
    <div className={`admin-shell min-h-screen pt-24 pb-16 ${dark ? 'admin-dark bg-slate-950' : 'bg-gray-50'}`}>
      <NoIndex />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between mb-8">
          <div>
            <h1 className={`text-4xl font-light ${dark ? 'text-white' : 'text-cm-primary'}`}>Admin Dashboard</h1>
            <p className={`text-sm mt-1 ${dark ? 'text-slate-400' : 'text-gray-500'}`}>Ingelogd als {user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-all border ${
                dark
                  ? 'border-slate-700 bg-slate-900 text-amber-300 hover:bg-slate-800'
                  : 'border-gray-300 bg-white text-slate-600 hover:bg-gray-100'
              }`}
              aria-label={dark ? 'Schakel naar licht' : 'Schakel naar donker'}
              title={dark ? 'Schakel naar licht' : 'Schakel naar donker'}
              data-testid="admin-theme-toggle"
            >
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              onClick={logout}
              className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border transition-all ${
                dark
                  ? 'border-slate-700 text-slate-200 hover:bg-slate-900'
                  : 'border-gray-300 hover:bg-white'
              }`}
              data-testid="admin-logout"
            >
              <LogOut size={16} /> Uitloggen
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          <TabButton active={tab === 'kleuren'} onClick={() => setTab('kleuren')} icon={Palette} label="Kleuren" testid="tab-kleuren" />
          <TabButton active={tab === 'teksten'} onClick={() => setTab('teksten')} icon={FileText} label="Teksten" testid="tab-teksten" />
          <TabButton active={tab === 'fotos'} onClick={() => setTab('fotos')} icon={Camera} label="Foto's" testid="tab-fotos" />
          <TabButton active={tab === 'team'} onClick={() => setTab('team')} icon={Users} label="Team" testid="tab-team" />
          <TabButton active={tab === 'cocktails'} onClick={() => setTab('cocktails')} icon={Wine} label="Cocktails" testid="tab-cocktails" />
          <TabButton active={tab === 'galerij'} onClick={() => setTab('galerij')} icon={Images} label="Galerij" testid="tab-galerij" />
          <TabButton active={tab === 'offertes'} onClick={() => setTab('offertes')} icon={Mail} label="Offertes" testid="tab-offertes" />
        </div>

        {tab === 'kleuren' && <ColorsTab />}
        {tab === 'teksten' && <TextsTab />}
        {tab === 'fotos' && <PhotosTab />}
        {tab === 'team' && <TeamTab />}
        {tab === 'cocktails' && <CocktailsTab />}
        {tab === 'galerij' && <GalerijTab />}
        {tab === 'offertes' && <OffertesTab />}
      </div>
    </div>
  );
};

export default AdminDashboard;
